import {
  AuthBindings,
  Authenticated,
  GitHubBanner,
  Refine,
} from "@refinedev/core";
import { RefineKbar, RefineKbarProvider } from "@refinedev/kbar";

import {
  ErrorComponent,
  notificationProvider,
  ThemedLayoutV2,
  ThemedTitleV2,
} from "@refinedev/antd";
import "@refinedev/antd/dist/reset.css";

import routerBindings, {
  CatchAllNavigate,
  NavigateToResource,
  UnsavedChangesNotifier,
} from "@refinedev/react-router-v6";
import dataProvider from "@refinedev/simple-rest";
import axios, { AxiosRequestConfig } from "axios";
import { AppIcon } from "components/app-icon";
import { CredentialResponse } from "interfaces/google";

import {FragmentTypesList, FragmentTypesShow, FragmentTypesCreate, FragmentTypesEdit} from "pages/fragmenttypes"
import {PublicationsList,PublicationsShow, PublicationsCreate, PublicationsEdit } from "pages/publications"
import {AutorPublicationsCreate,AutorPublicationsEdit,AutorPublicationsList,AutorPublicationsShow } from "pages/autorpublications"
import {AutorSectionPublicationsCreate,AutorSectionPublicationsEdit,AutorSectionPublicationsList,AutorSectionPublicationsShow } from "pages/autorSectionPublications"
import {SectionPubsCreate,SectionPubsEdit,SectionPubsList,SectionPubsShow } from "pages/sectionpubs"
import {FragmentPubsCreate,FragmentPubsEdit,FragmentPubsList,FragmentPubsShow } from "pages/fragmentpubs"
import {SubsectionPubsCreate,SubsectionPubsEdit,SubsectionPubsList,SubsectionPubsShow } from "pages/subsectionpubs"
import {FragmentObjectsCreate,FragmentObjectsEdit,FragmentObjectsList,FragmentObjectsShow } from "pages/fragmentobjects"

import {InfografiasCreate,InfografiasEdit,InfografiasList,InfografiasShow } from "pages/infografias"
import {SeccionInfografiasCreate,SeccionInfografiasEdit,SeccionInfografiasList,SeccionInfografiasShow } from "pages/seccionInfografias"
// import {SeccionInfografiasCreate,SeccionInfografiasEdit,SeccionInfografiasList,SeccionInfografiasShow } from "pages/seccionInfografias"
import {CopyRightsCreate,CopyRightsEdit,CopyRightsList,CopyRightsShow } from "pages/copyRights"

import { Login } from "pages/login";
import { useTranslation } from "react-i18next";
import { BrowserRouter, Outlet, Route, Routes, Link } from "react-router-dom";
import { parseJwt } from "utils/parse-jwt";
import { Header } from "./components/header";
import { ColorModeContextProvider } from "./contexts/color-mode";
import { BgColorsOutlined, AlertTwoTone } from "@ant-design/icons";
import { useEffect,useState } from 'react';
import SchemaDBProvider from "contexts/schemaDB/schemaDBProvider";
import { config } from "config";

import { ConfigProvider, Layout as AntdLayout, Space } from "antd";
import { CustomSider } from "components/siders";
import Home from "pages/home/Home";
import ListResources from "pages/resources/list";
import CreateResources from "pages/resources/create";
import EditResources from "pages/resources/edit";
import CreateSlideInfografias from "pages/slideInfografias/create";
import { BibliotecaVirtualCreate, BibliotecaVirtualList, BibliotecaVirtualShow, BVEdit } from "pages/bibliotecaVirtual";

const axiosInstance = axios.create();
axiosInstance.interceptors.request.use((request: AxiosRequestConfig) => {
  const token = localStorage.getItem("token");
  if (request.headers) {
    request.headers["Authorization"] = `Bearer ${token}`;
  } else {
    request.headers = {
      Authorization: `Bearer ${token}`,
    };
  }

  return request;
});

function App() {
  const { t, i18n } = useTranslation();

  const authProvider: AuthBindings = {
    login: async ({ credential }: CredentialResponse) => {
      const profileObj = credential ? parseJwt(credential) : null;

      if(!(profileObj?.email.includes("ingco.co") || profileObj?.email.includes("lugnia.com"))) return{
          success: false,
          redirectTo: "/",
        };

      if (profileObj) {
        localStorage.setItem(
          "user",
          JSON.stringify({
            ...profileObj,
            avatar: profileObj.picture,
          })
        );

        localStorage.setItem("token", `${credential}`);

        return {
          success: true,
          redirectTo: "/",
        };
      }

      return {
        success: false,
      };
    },
    logout: async () => {
      const token = localStorage.getItem("token");

      if (token && typeof window !== "undefined") {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        axios.defaults.headers.common = {};
        window.google?.accounts.id.revoke(token, () => {
          return {};
        });
      }

      return {
        success: true,
        redirectTo: "/login",
      };
    },
    onError: async (error) => {
      console.error(error);
      return { error };
    },
    check: async () => {
      const token = localStorage.getItem("token");

      if (token) {
        return {
          authenticated: true,
        };
      }

      return {
        authenticated: false,
        error: {
          message: "Check failed",
          name: "Token not found",
        },
        logout: true,
        redirectTo: "/login",
      };
    },
    getPermissions: async () => null,
    getIdentity: async () => {
      const user = localStorage.getItem("user");
      if (user) {
        return JSON.parse(user);
      }

      return null;
    },
  };

  const i18nProvider = {
    translate: (key: string, params: object) => t(key, params),
    changeLocale: (lang: string) => i18n.changeLanguage(lang),
    getLocale: () => i18n.language,
  };

  const [menuHorizontal, setMenuHorizontal] = useState(true)

  return (
    <SchemaDBProvider>
      <BrowserRouter>
      <RefineKbarProvider>
        <ColorModeContextProvider>
          <Refine
            // dataProvider={dataProvider("https://api.fake-rest.refine.dev")}

            dataProvider={dataProvider(config.urlAPI)}
            notificationProvider={notificationProvider}
            authProvider={authProvider}
            i18nProvider={i18nProvider}
            routerProvider={routerBindings}
            resources={[
              {
                name: "bibliotecaVirtual",
                list:   "/bibliotecaVirtual",
                create: "/bibliotecaVirtual/create",
                edit:   "/bibliotecaVirtual/edit/:id",
                show:   "/bibliotecaVirtual/show/:id",
                meta: {
                  label: "Biblioteca Virtual"
                  }   				
              },
              {
                name: "publication",
                list: "/publications",
                create: "/publications/create",
                edit: "/publications/edit/:id",
                show: "/publications/show/:id",
                meta: {
                  label: "Publicaciones"
                }              
              }, 
              {
                name: "autorPublication",
                list:   "/autorPublications",
                create: "/autorPublications/create",
                edit:   "/autorPublications/edit/:id",
                show:   "/autorPublications/show/:id",
                meta: {
                  label: "Autor_Publicacion"
                  , hide: true
                }               
              }, 
              {
                name: "autorSectionPublication",
                list:   "/autorSectionPublications",
                create: "/autorSectionPublications/create",
                edit:   "/autorSectionPublications/edit/:id",
                show:   "/autorSectionPublications/show/:id",
                meta: {
                  label: "Autor_Sección"
                  , hide: true
                }               
              }, 
              {
                name: "sectionPub",
                list:   "/sectionPubs",
                create: "/sectionPubs/create",
                edit:   "/sectionPubs/edit/:id",
                show:   "/sectionPubs/show/:id",
                meta: {
                  label: "SectionPub"
                  , hide: true
                  }   				
              }, 			
              {
                name: "fragmentPub",
                list:   "/fragmentPubs",
                create: "/fragmentPubs/create",
                edit:   "/fragmentPubs/edit/:id",
                show:   "/fragmentPubs/show/:id",
                meta: {
                  label: "Fragmento"
                  , hide: true
                  }   				
              },
              {
                name: "subsectionPub",
                list:   "/subsectionPubs",
                create: "/subsectionPubs/create",
                edit:   "/subsectionPubs/edit/:id",
                show:   "/subsectionPubs/show/:id",
                meta: {
                  label: "SubsectionPub"
                  , hide: true
                  }   				
              },
              {
                name: "fragmentObject",
                list:   "/fragmentObjects",
                create: "/fragmentObjects/create",
                edit:   "/fragmentObjects/edit/:id",
                show:   "/fragmentObjects/show/:id",
                meta: {
                  label: "Objeto"
                  , hide: true
                }   				
              },
              {
                name: "infografia",
                list:   "/infografias",
                create: "/infografias/create",
                edit:   "/infografias/edit/:id",
                show:   "/infografias/show/:id",
                meta: {
                  label: "Infografia"
                  }   				
              },
              {
                name: "seccionInfografia",
                list:   "/seccionInfografias",
                create: "/seccionInfografias/create",
                edit:   "/seccionInfografias/edit/:id",
                show:   "/seccionInfografias/show/:id",
                meta: {
                  label: "Seccion"
                  , hide : true
                  }   				
              },
              {
                name: "fragmentType",
                list: "/fragmentTypes",
                create: "/fragmentTypes/create",
                edit: "/fragmentTypes/edit/:id",
                show: "/fragmentTypes/show/:id",
                meta: {
                  label: "Tipos de Fragmento"

                  // , canDelete: true,
                  // , icon : <AlertTwoTone twoToneColor={"#ff00ff"} />
                }
              },
              {
                name: "recursoInfografia",
                list: "/recursoInfografia",
                create: "/recursoInfografia/create",
                edit: "/recursoInfografia/edit/:id",
                meta: {
                  label: "Recursos Infografía"
                  , hide : true
                }
                // show: "/recursoInfografia/edit/:id",
              }


              ,{
                name:    "InfProyecto",
                list:   "/InfProyecto",
                create: "/InfProyecto/create",
                edit:   "/InfProyecto/edit/:id",
                show:   "/InfProyecto/show/:id",
                meta: {
                  label: "Proyectos"
                  // , canDelete: true,
                  // , icon : <AlertTwoTone twoToneColor={"#ff00ff"} />
                  , hide : true
                }
              }              
              ,{
                name:    "InfTemplate",
                list:   "/InfTemplate",
                create: "/InfTemplate/create",
                edit:   "/InfTemplate/edit/:id",
                show:   "/InfTemplate/show/:id",
                meta: {
                  label: "Templates"
                  , hide : true
                  // , canDelete: true,
                  // , icon : <AlertTwoTone twoToneColor={"#ff00ff"} />

                }
              }              
              ,{
                name:    "InfFuente",
                list:   "/InfFuente",
                create: "/InfFuente/create",
                edit:   "/InfFuente/edit/:id",
                show:   "/InfFuente/show/:id",
                meta: {
                  label: "Fuentes"
                  , hide : true

                  // , canDelete: true,
                  // , icon : <AlertTwoTone twoToneColor={"#ff00ff"} />
                }
              }              
              ,{
                name:    "InfTipo",
                list:   "/InfTipo",
                create: "/InfTipo/create",
                edit:   "/InfTipo/edit/:id",
                show:   "/InfTipo/show/:id",
                meta: {
                  label: "Tipos"
                  , hide : true

                  // , canDelete: true,
                  // , icon : <AlertTwoTone twoToneColor={"#ff00ff"} />
                }
              }              
              ,{
                name:    "TemplateInfografia",
                list:   "/TemplateInfografia",
                create: "/TemplateInfografia/create",
                edit:   "/TemplateInfografia/edit/:id",
                show:   "/TemplateInfografia/show/:id",
                meta: {
                  label: "Plantillas Slide"
                  , hide : true

                  // , canDelete: true,
                  // , icon : <AlertTwoTone twoToneColor={"#ff00ff"} />
                }
              }     

            , {
              name: "copyRight",
              list:   "/copyRights",
              create: "/copyRights/create",
              edit:   "/copyRights/edit/:id",
              show:   "/copyRights/show/:id",
              meta: {
                label: "CopyRight"
                // , canDelete: true,
                // , icon : <AlertTwoTone twoToneColor={"#ff00ff"} />
                }   				
            }
			                       
            ]}
            options={{
              syncWithLocation: true,
              warnWhenUnsavedChanges: true,
              // liveMode: "auto"
            }}
          >
            <Routes>
              <Route
                element={
                  <Authenticated fallback={<CatchAllNavigate to="/login" />}>
                    {
                      !menuHorizontal?<ThemedLayoutV2
                      Header={Header}
                      Title={({ collapsed }) => (
                        <ThemedTitleV2
                          collapsed={collapsed}
                          text="Verbo8"
                          icon={<AppIcon />}
                        />
                      )}
                    ></ThemedLayoutV2>:
                    <ThemedLayoutV2
                        Sider={() => null}
                        Title={({ collapsed }) => (
                          <ThemedTitleV2
                            collapsed={collapsed}
                            text="Verbo8"
                            icon={<AppIcon />}
                          />
                        )}
                        Header={() => {
                            return (
                                <AntdLayout.Header>
                                  <Space>

                                    <Link
                                        to="/"
                                        style={{
                                            float: "left",
                                            marginRight: "10px",
                                        }}
                                    >
                                        <img
                                            src="/refine.svg"
                                            alt="Refine"
                                            style={{
                                                width: "100px",
                                            }}
                                        />
                                    </Link>
                                    <CustomSider />
                                    
                                    {/* <Link to="/a">AAA</Link> */}

                                  </Space>
                                </AntdLayout.Header>
                            );
                        }}
                    >                    
                      <Outlet />
                    </ThemedLayoutV2>                    
                    }
                  </Authenticated>
                }
              >


                <Route
                    path="/a"
                    element={<h1>Hola</h1>}
                />

                <Route
                    index
                    element={<Home></Home>}
                />

                <Route path="/fragmentTypes">
                  <Route index element={<FragmentTypesList />} />
                  <Route path="create" element={<FragmentTypesCreate />} />
                  <Route path="edit/:id" element={<FragmentTypesEdit />} />
                  <Route path="show/:id" element={<FragmentTypesShow />} />
                </Route>

                <Route path="/bibliotecaVirtual">
                  <Route index element={<BibliotecaVirtualList />} />
                  <Route path="create" element={<BibliotecaVirtualCreate />} />
                  <Route path="edit/:id" element={<BVEdit />} />
                  <Route path="show/:id" element={<BibliotecaVirtualShow />} />
                </Route>

                <Route path="/publications">
                  <Route index            element={<PublicationsList />} />
                  <Route path="create"    element={<PublicationsCreate />} />
                  <Route path="edit/:id"  element={<PublicationsEdit />} />
                  <Route path="show/:id"  element={<PublicationsShow />} />
                </Route>

                <Route path="/autorPublications">
                  <Route index            element={<AutorPublicationsList />} />
                  <Route path="create"    element={<AutorPublicationsCreate />} />
                  <Route path="edit/:id"  element={<AutorPublicationsEdit />} />
                  <Route path="show/:id"  element={<AutorPublicationsShow />} />
                </Route>

                <Route path="/autorSectionPublications">
                  <Route index            element={<AutorSectionPublicationsList />} />
                  <Route path="create"    element={<AutorSectionPublicationsCreate />} />
                  <Route path="edit/:id"  element={<AutorSectionPublicationsEdit />} />
                  <Route path="show/:id"  element={<AutorSectionPublicationsShow />} />
                </Route>
			
                <Route path="/sectionPubs">
                  <Route index            element={<SectionPubsList />} />
                  <Route path="create"    element={<SectionPubsCreate />} />
                  <Route path="edit/:id"  element={<SectionPubsEdit />} />
                  <Route path="show/:id"  element={<SectionPubsShow />} />
                </Route>
                    
                <Route path="/fragmentPubs">
                  <Route index            element={<FragmentPubsList />} />
                  <Route path="create"    element={<FragmentPubsCreate />} />
                  <Route path="edit/:id"  element={<FragmentPubsEdit />} />
                  <Route path="show/:id"  element={<FragmentPubsShow />} />
                </Route>
			                     
                <Route path="/subsectionPubs">
                  <Route index            element={<SubsectionPubsList />} />
                  <Route path="create"    element={<SubsectionPubsCreate />} />
                  <Route path="edit/:id"  element={<SubsectionPubsEdit />} />
                  <Route path="show/:id"  element={<SubsectionPubsShow />} />
                </Route>

                <Route path="/fragmentObjects">
                  <Route index            element={<FragmentObjectsList />} />
                  <Route path="create"    element={<FragmentObjectsCreate />} />
                  <Route path="edit/:id"  element={<FragmentObjectsEdit />} />
                  <Route path="show/:id"  element={<FragmentObjectsShow />} />
                </Route>

                <Route path="/infografias">
                  <Route index            element={<InfografiasList />} />
                  <Route path="create"    element={<InfografiasCreate />} />
                  <Route path="edit/:id"  element={<InfografiasEdit />} />
                  <Route path="show/:id"  element={<InfografiasShow />} />
                </Route>

                <Route path="/seccionInfografias">
                  <Route index            element={<SeccionInfografiasList />} />
                  <Route path="create"    element={<SeccionInfografiasCreate />} />
                  <Route path="edit/:id"  element={<SeccionInfografiasEdit />} />
                  <Route path="show/:id"  element={<SeccionInfografiasShow />} />
                </Route>
  
                <Route path="/copyRights">
                  <Route index            element={<CopyRightsList />} />
                  <Route path="create"    element={<CopyRightsCreate />} />
                  <Route path="edit/:id"  element={<CopyRightsEdit />} />
                  <Route path="show/:id"  element={<CopyRightsShow />} />
                </Route>
              
              
                <Route path="/recursoInfografia">
                  <Route index            element={<ListResources />} />
                  <Route path="create"    element={<CreateResources />} />
                  <Route path="edit/:id"  element={<EditResources />} />
                  <Route path="show/:id"  element={<SeccionInfografiasShow />} />
                </Route>
                <Route path="/slide">
                  <Route path="create" element={<CreateSlideInfografias/>}/>
                </Route>

              </Route>
              <Route
                element={
                  <Authenticated fallback={<Outlet />}>
                    <NavigateToResource />
                  </Authenticated>
                }
              >
                <Route path="/login" element={<Login />} />
              </Route>
              <Route
                element={
                  <Authenticated>
                    <ThemedLayoutV2
                      Header={Header}
                      Title={({ collapsed }) => (
                        <ThemedTitleV2
                          collapsed={collapsed}
                          text="Verbo8"
                          icon={<AppIcon />}
                        />
                      )}
                    >
                      <Outlet />
                    </ThemedLayoutV2>
                  </Authenticated>
                }
              >
                <Route path="*" element={<ErrorComponent />} />
              </Route>
            </Routes>

            {/* <RefineKbar /> */}
            <UnsavedChangesNotifier />
          </Refine>
        </ColorModeContextProvider>
      </RefineKbarProvider>
    </BrowserRouter>
    </SchemaDBProvider>
  );
}

export default App;
